import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import {
  AUTO_STREAM_FREE_ATTENDEE_LIMIT,
  FREE_TRIAL_ATTENDEES_PER_STREAM,
  FREE_TRIAL_DAYS,
  FREE_TRIAL_STREAMS,
  formatCents,
} from "@/lib/trial";

type BillingDashboard = {
  pricing: {
    activeStreamPriceCents: number;
    attendeePriceCents: number;
    freeTrialStreams: number;
    freeTrialDays: number;
    freeTrialAttendeesPerStream: number;
    autoStreamFreeAttendeeLimit?: number;
  };
  billing: {
    status: string;
    trialEndsAt?: number | null;
    trialDaysRemaining?: number | null;
    currentPeriodStart: number;
    currentPeriodEnd: number;
  };
  usage: {
    activeStreams: number;
    freeAutoStreams?: number;
    streamChargesCents: number;
    attendeesProcessed: number;
    usageChargesCents: number;
    estimatedTotalCents: number;
  };
  streams: Array<{
    streamId: string;
    name: string;
    lifecycle: string;
    origin?: string;
    billingExempt?: boolean;
    attendeesProcessed: number;
    trialAttendeesUsed: number;
    trialAttendeeLimit: number;
    trialUsageLevel: "ok" | "warning" | "exhausted";
  }>;
};

function billingStatusTone(
  status: string,
): "syncing" | "warning" | "paused" | "ready" | "neutral" {
  switch (status) {
    case "active":
      return "syncing";
    case "trialing":
      return "ready";
    case "past_due":
      return "warning";
    case "canceled":
      return "paused";
    default:
      return "neutral";
  }
}

export function BillingPanel({ showTitle = false }: { showTitle?: boolean }) {
  const { orgId, canManage } = useOrg();
  const [data, setData] = useState<BillingDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    void api<BillingDashboard>(`/api/accounts/${orgId}/billing`)
      .then(setData)
      .catch((e) =>
        setMessage(e instanceof Error ? e.message : "Could not load billing"),
      )
      .finally(() => setLoading(false));
  }, [orgId]);

  async function startCheckout() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api<{ url: string }>(
        `/api/accounts/${orgId}/billing/checkout`,
        {
          method: "POST",
          body: "{}",
        },
      );
      window.location.href = result.url;
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Could not start checkout");
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api<{ url: string }>(
        `/api/accounts/${orgId}/billing/portal`,
        {
          method: "POST",
          body: "{}",
        },
      );
      window.location.href = result.url;
    } catch (e) {
      setMessage(
        e instanceof Error ? e.message : "Could not open billing portal",
      );
      setBusy(false);
    }
  }

  if (loading) return <PageSkeleton />;

  const isTrial = data?.billing.status === "trialing";
  const isPaid = data?.billing.status === "active";

  return (
    <div className="space-y-5">
      {showTitle ? (
        <div>
          <h2 className="text-xl font-semibold text-rs-ink">Billing</h2>
          <p className="text-sm text-rs-ink-muted">
            ${((data?.pricing.activeStreamPriceCents ?? 1000) / 100).toFixed(0)}{" "}
            per billable active stream / month (subscription seats; quantity
            updates when streams change, with Stripe prorations) plus $
            {((data?.pricing.attendeePriceCents ?? 5) / 100).toFixed(2)} per
            attendee processed (metered on the invoice). With Auto-Create
            Streams (trial or paid), auto-created streams with up to{" "}
            {data?.pricing.autoStreamFreeAttendeeLimit ??
              AUTO_STREAM_FREE_ATTENDEE_LIMIT}{" "}
            attendees are free.
          </p>
        </div>
      ) : (
        <p className="text-sm text-rs-ink-muted">
          ${((data?.pricing.activeStreamPriceCents ?? 1000) / 100).toFixed(0)}{" "}
          per billable active stream / month (subscription seats with
          prorations) plus $
          {((data?.pricing.attendeePriceCents ?? 5) / 100).toFixed(2)} per
          attendee processed (metered). Free trial: {FREE_TRIAL_DAYS} days, up
          to {FREE_TRIAL_STREAMS} streams, {FREE_TRIAL_ATTENDEES_PER_STREAM}{" "}
          attendees per stream. Auto-Create Streams (trial or paid) with up to{" "}
          {AUTO_STREAM_FREE_ATTENDEE_LIMIT} attendees are free.
        </p>
      )}

      {data ? (
        <>
          <Panel className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm text-rs-ink-muted">Subscription Status</p>
                <div className="mt-1 flex items-center gap-2">
                  <StatusBadge tone={billingStatusTone(data.billing.status)}>
                    {data.billing.status}
                  </StatusBadge>
                  {isTrial && data.billing.trialDaysRemaining != null ? (
                    <span className="text-sm text-rs-ink-muted">
                      {data.billing.trialDaysRemaining} day
                      {data.billing.trialDaysRemaining === 1 ? "" : "s"}{" "}
                      remaining
                    </span>
                  ) : null}
                </div>
              </div>
              {canManage ? (
                <div className="flex gap-2">
                  {isPaid ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => void openPortal()}
                    >
                      Manage Billing
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => void startCheckout()}
                    >
                      {isTrial ? "Upgrade To Paid" : "Start Paid Billing"}
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </Panel>

          <Panel className="p-5">
            <p className="text-sm text-rs-ink-muted">Current Period Estimate</p>
            <p className="mt-1 text-xs text-rs-ink-subtle">
              {new Date(
                data.billing.currentPeriodStart * 1000,
              ).toLocaleDateString()}{" "}
              –{" "}
              {new Date(
                data.billing.currentPeriodEnd * 1000,
              ).toLocaleDateString()}
            </p>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-sm text-rs-ink-muted">
                  Billable Active Streams
                </dt>
                <dd className="text-lg font-semibold text-rs-ink">
                  {data.usage.activeStreams}
                </dd>
                <dd className="text-sm text-rs-ink-muted">
                  Stream charges: {formatCents(data.usage.streamChargesCents)}
                </dd>
                {(data.usage.freeAutoStreams ?? 0) > 0 ? (
                  <dd className="mt-1 text-sm text-rs-ink-muted">
                    Free auto streams (≤
                    {data.pricing.autoStreamFreeAttendeeLimit ??
                      AUTO_STREAM_FREE_ATTENDEE_LIMIT}{" "}
                    attendees): {data.usage.freeAutoStreams}
                  </dd>
                ) : null}
              </div>
              <div>
                <dt className="text-sm text-rs-ink-muted">
                  Attendees Processed
                </dt>
                <dd className="text-lg font-semibold text-rs-ink">
                  {data.usage.attendeesProcessed.toLocaleString()}
                </dd>
                <dd className="text-sm text-rs-ink-muted">
                  Usage charges: {formatCents(data.usage.usageChargesCents)}
                </dd>
              </div>
            </dl>
            <p className="mt-4 text-base font-semibold text-rs-ink">
              Estimated Total: {formatCents(data.usage.estimatedTotalCents)}
            </p>
            {isPaid ? (
              <p className="mt-2 text-sm text-rs-ink-muted">
                Stream seats bill from your current subscription quantity
                (prorated when it changes). Attendee charges are metered and
                appear on the Stripe invoice for this period.
              </p>
            ) : null}
            {isTrial ? (
              <p className="mt-2 text-sm text-rs-ink-muted">
                Trial usage is not billed until you upgrade. Limits:{" "}
                {FREE_TRIAL_STREAMS} streams, {FREE_TRIAL_ATTENDEES_PER_STREAM}{" "}
                attendees per stream.
              </p>
            ) : null}
          </Panel>

          {isTrial ? (
            <Panel>
              <PanelHeader title="Trial Usage By Stream" />
              <ul className="divide-y divide-rs-border">
                {data.streams.map((s) => (
                  <li
                    key={s.streamId}
                    className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
                  >
                    <div>
                      <p className="font-medium text-rs-ink">{s.name}</p>
                      <p className="text-sm text-rs-ink-muted capitalize">
                        {s.lifecycle}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={
                          s.trialUsageLevel === "exhausted"
                            ? "text-sm font-medium text-rs-danger"
                            : s.trialUsageLevel === "warning"
                              ? "text-sm font-medium text-rs-warning"
                              : "text-sm text-rs-ink-muted"
                        }
                      >
                        {s.trialAttendeesUsed} / {s.trialAttendeeLimit} trial
                        attendees used
                      </p>
                      {s.trialUsageLevel === "exhausted" ? (
                        <p className="text-xs text-rs-danger">
                          Trial allowance reached — upgrade to continue
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
                {data.streams.length === 0 ? (
                  <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                    No streams yet.
                  </li>
                ) : null}
              </ul>
            </Panel>
          ) : isPaid ? (
            <Panel>
              <PanelHeader title="Streams This Period" />
              <ul className="divide-y divide-rs-border">
                {data.streams.map((s) => (
                  <li
                    key={s.streamId}
                    className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
                  >
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-rs-ink">{s.name}</p>
                        {s.origin === "auto" ? (
                          <StatusBadge tone="scheduled">Auto</StatusBadge>
                        ) : null}
                        {s.origin === "auto" && s.billingExempt ? (
                          <StatusBadge tone="ready">
                            Free (≤{AUTO_STREAM_FREE_ATTENDEE_LIMIT})
                          </StatusBadge>
                        ) : null}
                      </div>
                      <p className="text-sm text-rs-ink-muted capitalize">
                        {s.lifecycle}
                      </p>
                    </div>
                    <p className="text-sm text-rs-ink-muted">
                      {s.attendeesProcessed.toLocaleString()} attendees
                      processed
                    </p>
                  </li>
                ))}
                {data.streams.length === 0 ? (
                  <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                    No streams yet.
                  </li>
                ) : null}
              </ul>
            </Panel>
          ) : null}
        </>
      ) : null}

      {message ? <p className="text-sm text-rs-danger">{message}</p> : null}
    </div>
  );
}

/** Standalone page (org admin). Workspace uses Settings → Billing. */
export function BillingPage() {
  return <BillingPanel showTitle />;
}
